import { useEffect, useState } from 'react';

/**
 * Micro-celebration: a short particle burst anchored to the viewport center.
 * Pure CSS animation (transform/opacity only), fully disabled for reduced
 * motion where it degrades to a soft fade. Fired sparingly — real milestones
 * only (review session milestones, completed focus blocks, finished rituals).
 */

interface Burst {
  id: number;
  particles: Array<{ x: number; rotate: number; hue: string; delay: number; distance: number }>;
}

let nextId = 1;
type Listener = (burst: Burst) => void;
const listeners = new Set<Listener>();

export function celebrate(): void {
  const burst: Burst = { id: nextId++, particles: buildParticles() };
  for (const listener of listeners) listener(burst);
}

function buildParticles(): Burst['particles'] {
  const palette = ['#d54a2f', '#2854c5', '#147a5b', '#9b6618', '#b12b3d'];
  return Array.from({ length: 14 }, (_, index) => ({
    x: (index / 13) * 2 - 1,
    rotate: Math.round(Math.random() * 260 - 130),
    hue: palette[index % palette.length]!,
    delay: Math.round(Math.random() * 60),
    distance: 90 + Math.round(Math.random() * 110)
  }));
}

export function CelebrationLayer() {
  const [bursts, setBursts] = useState<Burst[]>([]);

  useEffect(() => {
    const listener: Listener = (burst) => {
      setBursts((current) => [...current.slice(-1), burst]);
      window.setTimeout(() => setBursts((current) => current.filter((entry) => entry.id !== burst.id)), 1150);
    };
    listeners.add(listener);
    return () => { listeners.delete(listener); };
  }, []);

  return (
    <div className="celebration-layer" aria-hidden="true">
      {bursts.map((burst) => (
        <div className="celebration-burst" key={burst.id}>
          {burst.particles.map((particle, index) => (
            <i
              key={index}
              style={{
                '--dx': `${particle.x * 190}px`,
                '--dy': `${-particle.distance}px`,
                '--rot': `${particle.rotate}deg`,
                '--delay': `${particle.delay}ms`,
                background: particle.hue
              } as React.CSSProperties}
            />
          ))}
        </div>
      ))}
    </div>
  );
}
